/**
 * WidgetFrame — [T149-PR8-CONT-11] public slot-based widget chrome primitive.
 *
 * Single source of widget chrome. Every body component is mounted inside
 * `<WidgetFrame>` — bodies must NEVER render their own `<Card>` or header.
 *
 * ## Architecture (slot composition, Radix-style)
 *
 * ```mermaid
 * graph TD
 *   WF[WidgetFrame<br/>public API<br/>= thin slot composer]
 *   HOOK[useWidgetFrameProps<br/>state → 4 slot props]
 *   CH[Chrome<br/>outer Card + glass toggle]
 *   HD[HeaderSlot<br/>sr-only h3 only]
 *   BD[BodySlot<br/>body + active overlay]
 *   OV[Overlays<br/>error / timeout / no-data / compat]
 *   FT[FooterSlot<br/>footnote + last-updated]
 *
 *   WF -->|derives| HOOK
 *   WF --> CH
 *   WF --> HD
 *   WF --> BD
 *   WF --> FT
 *   HOOK -->|chrome| CH
 *   HOOK -->|header| HD
 *   HOOK -->|body  | BD
 *   HOOK -->|footer| FT
 *   BD --> OV
 * ```
 *
 * ## Slot anatomy (top → bottom)
 *   1. Chrome     — `<Card role="region" data-testid="widget-frame">` shell
 *   2. HeaderSlot — sr-only `<h3>` only (headerless post-[T-DASHBOARD-HEADERLESS])
 *   3. BodySlot   — body region + compatibility banner + active overlay
 *   4. Overlays   — error / timeout / no-data / compat (rendered inside BodySlot)
 *   5. FooterSlot — optional footnote + last-updated signal
 *
 * ## Backwards compatibility (T-DASHBOARD-HEADERLESS, headerless migration)
 * The public `WidgetFrame` component is a drop-in replacement for the
 * pre-refactor monolithic `WidgetFrame.tsx`. All 40+ skin consumers
 * continue to work without modification:
 *
 *   - Identical prop signatures (`FrameProps`)
 *   - Identical default values (`noGlass = true`, `isRefreshing = false`, …)
 *   - Identical ARIA attributes (`aria-busy`, `aria-labelledby`, `role="region"`)
 *   - Identical Tailwind utility strings
 *
 * ⚠️  BREAKING DOM CHANGE: `HeaderSlot` is now **headerless**. The
 * `data-testid="widget-header"` element (icon + h3 title + actions
 * cluster) is GONE. Tests/selectors that target it must be rewritten
 * against the new contract (sr-only h3 + visual chrome gestures).
 * The following `data-testid` values are also removed:
 *   - `widget-header`           — outer visible header row
 *   - `widget-icon`             — icon circle container
 *   - `widget-title`            — visible `<h3>` title
 *   - `widget-actions`          — right-aligned actions cluster
 *   - `widget-refreshing-spinner` — Loader2 during refresh
 *
 * The `aria-labelledby={headerId}` chain on the outer Card is preserved
 * — `headerId` now points at the sr-only `<h3>` (the ONLY DOM HeaderSlot
 * emits).
 * @see https://www.radix-ui.com/primitives/docs/primitives — slot pattern
 * @see https://react.dev/reference/react — composition
 * @see docs/design/T149-PR8-CONT-11-WIDGET-STANDARDIZATION.md §2
 */

import { Chrome } from './Chrome';
import { HeaderSlot } from './HeaderSlot';
import { BodySlot } from './BodySlot';
import { FooterSlot } from './FooterSlot';
import { useWidgetFrameProps } from './useWidgetFrameProps';
import type { FrameProps } from './types';

// Re-export public types so consumers (and `_shared.tsx` test helpers) can
// reference `WidgetFrameProps` from the same module path they already use.
export type { FrameProps as WidgetFrameProps } from './types';
export type {
  BodyState,
  ChromeState,
  FooterState,
  HeaderState,
  ViewModeRecoveryProps,
} from './types';

/**
 * Public `WidgetFrame` — thin slot composer.
 *
 * Accepts the IDENTICAL `WidgetFrameProps` shape as the pre-refactor
 * monolithic component. Forwards state to `useWidgetFrameProps` which
 * derives the four slot prop bundles. Then renders:
 *
 *   <Chrome>
 *     <HeaderSlot />
 *     <BodySlot />
 *     <FooterSlot />
 *   </Chrome>
 */
export function WidgetFrame(props: FrameProps) {
  const { chrome, header, body, footer, headerId } = useWidgetFrameProps(props);
  return (
    <Chrome {...chrome} headerId={headerId}>
      <HeaderSlot {...header} />
      <BodySlot {...body} />
      <FooterSlot {...footer} />
    </Chrome>
  );
}

export default WidgetFrame;
